/**
 * One document's scorecard: each principle's verdict and summary, and for a
 * concern or fail the findings — what is wrong, the verified quote with a
 * citation chip that opens the source at the page, and the suggested fix.
 */

import { Alert, Blockquote, Group, Paper, Stack, Text, Title } from "@mantine/core";
import { CitationChip } from "@/features/solutions/shared";
import { QualityStatusChip } from "./QualityStatusChip";
import { PRINCIPLE_LABEL, type QualityDocument, type QualityPrinciple } from "./types";

function PrincipleCard({ p }: { p: QualityPrinciple }) {
  return (
    <Paper withBorder radius="md" p="sm" data-testid={`principle-${p.principle}`}>
      <Stack gap={6}>
        <Group gap="xs">
          <Text fw={600}>{PRINCIPLE_LABEL[p.principle]}</Text>
          <QualityStatusChip status={p.status} />
        </Group>
        <Text size="sm">{p.summary}</Text>
        {p.findings.map((f) => (
          <Stack key={f.citation.n} gap={4} pl="sm">
            <Text size="sm" fw={500}>
              {f.description}
            </Text>
            <Blockquote p="xs" radius="sm" fz="sm" color="gray">
              {f.quote}
            </Blockquote>
            <Group gap="xs">
              <CitationChip citation={f.citation} />
            </Group>
            <Text size="sm">
              <Text span fw={600}>
                Suggested fix:{" "}
              </Text>
              {f.suggested_fix}
            </Text>
          </Stack>
        ))}
        {p.dropped_findings > 0 && (
          <Text size="xs" c="dimmed">
            {p.dropped_findings} finding(s) dropped: the quoted text was not in the document.
          </Text>
        )}
      </Stack>
    </Paper>
  );
}

export function QualityDocumentDetail({ doc }: { doc: QualityDocument }) {
  return (
    <Stack gap="sm" data-testid="quality-document">
      <Group gap="xs">
        <Title order={5}>{doc.display_name}</Title>
        <QualityStatusChip status={doc.overall} />
        {doc.sections > 1 && (
          <Text size="xs" c="dimmed">
            Long document: assessed in {doc.sections} sections
          </Text>
        )}
      </Group>
      {doc.error ? (
        <Alert color="gray" title="Not assessed">
          {doc.error}
        </Alert>
      ) : (
        <>
          {doc.notes.map((note) => (
            <Alert key={note} color="yellow" variant="light">
              {note}
            </Alert>
          ))}
          {doc.principles.map((p) => (
            <PrincipleCard key={p.principle} p={p} />
          ))}
        </>
      )}
    </Stack>
  );
}
